import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function VerifyInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [invitationData, setInvitationData] = useState<any>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link. No token provided.');
      return;
    }

    verifyInvitation(token);
  }, [searchParams]);

  const verifyInvitation = async (token: string) => {
    try {
      console.log('Verifying invitation token...');
      
      const { data, error } = await supabase.functions.invoke('verify-user-invitation', {
        body: { token },
      });

      if (error) throw error;

      if (data.error) {
        setStatus('error');
        setMessage(data.error);
        return;
      }

      console.log('Invitation verified, using recovery link...');
      console.log('Recovery link received:', data.recoveryLink);
      setInvitationData(data);

      // Automatically use the recovery link to authenticate
      if (data.recoveryLink) {
        console.log('Redirecting to recovery link...');
        
        // Add a timeout to detect if redirect fails
        const redirectTimeout = setTimeout(() => {
          console.error('Redirect appears to be stuck');
          setStatus('error');
          setMessage('Redirect failed. Please try clicking the link again or contact support.');
        }, 3000);
        
        try {
          window.location.href = data.recoveryLink;
        } catch (redirectError) {
          clearTimeout(redirectTimeout);
          console.error('Redirect error:', redirectError);
          setStatus('error');
          setMessage('Failed to redirect. Please try opening the link in a new tab.');
        }
      } else {
        console.error('No recovery link in response');
        setStatus('error');
        setMessage('Failed to generate authentication link');
      }
    } catch (error: any) {
      console.error('Error verifying invitation:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to verify invitation. Please try again or contact support.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {status === 'loading' && 'Verifying Your Invitation'}
            {status === 'success' && 'Invitation Verified!'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Please wait while we verify your invitation...'}
            {status === 'success' && 'Redirecting you to set up your account...'}
            {status === 'error' && message}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {status === 'loading' && (
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <p className="text-sm text-muted-foreground text-center">
                Welcome, {invitationData?.firstName}! Setting up your account...
              </p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 text-destructive" />
              <Button onClick={() => navigate('/auth')} className="w-full">
                Go to Login
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
